import React from 'react'

import { ShowModal, useModalContext } from 'containers/ModalManager'

// Components
import {
  Button,
  Truncate,
  Table,
  TableComponents,
  TabPanel,
  Tabs,
  Tab,
  TabList,
  TimeAgo,
} from '@sketch/components'

// Styles
import { NameAndExpiryContainer, TableWrapper } from './TokenList.styles'

// Local Components
import TokenDetailsModal from '../../modals/TokenDetailsModal'
import DeleteTokenConfirmationModal from '../../modals/DeleteTokenConfirmationModal'

import { PersonalTokenFragment } from '@sketch/gql-types'

import { dateFormat } from '@sketch/utils'

const localizeDate = (dateStr: string): string => dateFormat(new Date(dateStr))

interface TokenTableProps {
  showModal: ShowModal
  tokens: PersonalTokenFragment[]
}

const TokenTable: React.FC<TokenTableProps> = ({ tokens, showModal }) => {
  const tableHeader = [
    { label: 'Name' },
    { label: 'Last Used At' },
    { label: 'Actions' },
  ]

  const handleDeleteClick = (token: PersonalTokenFragment) => {
    showModal(DeleteTokenConfirmationModal, { token })
  }

  const handleRowClick = (token: PersonalTokenFragment) => {
    showModal(TokenDetailsModal, { token })
  }

  const tokenRow = (token: PersonalTokenFragment) => {
    const now = new Date()
    const expired = !!token.expiresAt && new Date(token.expiresAt) <= now

    const expiresAtPrefix = expired ? 'Expired on' : 'Valid until'
    const expiresAt = token.expiresAt
      ? `${expiresAtPrefix} ${localizeDate(token.expiresAt)}`
      : ''

    const lastUsedAt = token.lastUsedAt ? (
      <>
        Used <TimeAgo date={token.lastUsedAt} />
      </>
    ) : (
      'Unused'
    )

    return (
      <TableComponents.TableRow
        data-testid={`token-row-${token.identifier}`}
        onClick={() => handleRowClick(token)}
      >
        <TableComponents.TableCell>
          <NameAndExpiryContainer expired={expired}>
            <span>
              <Truncate width={130}>{token.description}</Truncate>
            </span>
            <span>{expiresAt}</span>
          </NameAndExpiryContainer>
        </TableComponents.TableCell>
        <TableComponents.TableCell>{lastUsedAt}</TableComponents.TableCell>
        <TableComponents.TableCell>
          <Button
            onClick={e => {
              e.stopPropagation()
              handleDeleteClick(token)
            }}
            variant={'tertiary'}
          >
            Delete...
          </Button>
        </TableComponents.TableCell>
      </TableComponents.TableRow>
    )
  }

  return (
    <TableWrapper>
      <Table
        header={tableHeader}
        items={tokens}
        renderItem={tokenRow}
        hideHeader
      />
    </TableWrapper>
  )
}

interface TokenListProps {
  tokens: PersonalTokenFragment[]
}

const dateDescOrder = (
  token1: PersonalTokenFragment,
  token2: PersonalTokenFragment
): number => {
  const date1: Date = new Date(token1.createdAt)
  const date2: Date = new Date(token2.createdAt)
  return date2.getTime() - date1.getTime()
}

const TokenList: React.FC<TokenListProps> = ({ tokens }) => {
  const now = new Date()

  // We show tokens ordered by their createdAt date, in descending order.
  // And all "active" tokens should appear before "expired" tokens.
  const sortedTokens = tokens.sort(dateDescOrder)
  const activeTokens = sortedTokens.filter(
    ({ expiresAt }) => expiresAt === null || new Date(expiresAt) > now
  )
  const expiredTokens = sortedTokens.filter(
    ({ expiresAt }) => expiresAt !== null && new Date(expiresAt) <= now
  )
  const allTokens = [...activeTokens, ...expiredTokens]

  const { showModal } = useModalContext()

  return (
    <Tabs>
      <TabList>
        <Tab>{tokens.length} Total</Tab>
        <Tab>{activeTokens.length} Active</Tab>
        <Tab>{expiredTokens.length} Expired</Tab>
      </TabList>
      <TabPanel>
        <TokenTable tokens={allTokens} showModal={showModal} />
      </TabPanel>
      <TabPanel>
        <TokenTable tokens={activeTokens} showModal={showModal} />
      </TabPanel>
      <TabPanel>
        <TokenTable tokens={expiredTokens} showModal={showModal} />
      </TabPanel>
    </Tabs>
  )
}

export default TokenList
